<template>
  <div>
    <!-- Trigger Button -->
    <v-btn 
      :disabled="disabled" 
      color="primary" 
      :loading="loading" 
      @click="fetch" 
      text
      class="font-weight-black text-uppercase tracking-widest rounded-lg px-4 transition-fast-in-fast-out h-44"
    >
      <v-icon left size="16">fas fa-file-export</v-icon>
      Exportar
    </v-btn>

    <!-- Download Dialog -->
    <v-dialog v-model="dataReady" max-width="440" transition="scale-transition">
      <v-card class="modern-card overflow-hidden rounded-xl">
        <!-- Premium Header -->
        <div class="bg-grey-light pa-6 border-bottom">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="40" class="mr-4 shadow-sm border-white-sm">
              <v-icon color="white" small>fas fa-download</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-black primary--text lh-1">Exportar Reporte Monetario</div>
              <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">Valores de OTs</div>
            </div>
          </div>
        </div>

        <v-card-text class="text-center pa-8">
          <v-avatar color="primary lighten-5" size="80" class="mb-6">
            <v-icon color="primary" size="40">fas fa-money-bill-wave</v-icon>
          </v-avatar>
          <div class="body-1 primary--text font-weight-bold mb-2">¡Reporte Preparado!</div>
          <div class="caption grey--text px-4 mb-8">Seleccione si desea exportar el listado resumido de montos o el detalle por producto.</div>

          <div class="d-flex flex-column gap-3">
            <vue-json-to-csv
              :json-data="workOrders"
              :labels="labels"
              :csv-title="csvTitle"
              :separator="';'"
              @success="val => handleSuccess(val)"
              @error="val => handleError(val)">
              <v-btn 
                color="success" 
                block 
                large 
                elevation="0"
                height="48"
                class="font-weight-black text-none rounded-lg shadow-sm"
              >
                <v-icon left>fas fa-list-ol</v-icon>
                Descargar Lista Resumen
              </v-btn>
            </vue-json-to-csv>

            <vue-json-to-csv
              :json-data="workOrdersDetail"
              :labels="labelsDetails"
              :csv-title="'detalle_'+csvTitle"
              :separator="';'"
              @success="val => handleSuccess(val)"
              @error="val => handleError(val)">
              <v-btn 
                color="primary" 
                block 
                large 
                outlined
                height="48"
                class="font-weight-black text-none rounded-lg"
              >
                <v-icon left>fas fa-info-circle</v-icon>
                Descargar Reporte Detallado
              </v-btn>
            </vue-json-to-csv>
          </div>
        </v-card-text>

        <v-divider class="mx-8 opacity-10"></v-divider>
        <div class="pa-4 text-center">
          <v-btn text small color="grey" class="text-none font-weight-bold" @click="dataReady = false">Cerrar</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {fetchWorkOrdersMoney} from '@/api/reports'
import VueJsonToCsv from 'vue-json-to-csv'
import moment from 'moment'

export default {
  name: "ExportWorkOrdersMoney",
  props: ['filter', 'options', 'disabled', 'search'],
  components: { VueJsonToCsv },
  data() {
    return {
      loading: false,
      workOrders: [],
      workOrdersDetail: [],
      dataReady: false,
      labels: { 
        id: { title: 'OT' },
        created_at: { title: 'Creada' },
        completed_at: { title: 'Completada' },
        sales_note__id: { title: 'Cotización' },
        sales_note__name: { title: 'Proyecto' },
        sales_note__customer__name: { title: 'Cliente' },
        sales_note__customer__seller__first_name: { title: 'Vendedor' },
        sales_note__origen: { title: 'Origen' },
        delivery_date: { title: 'Fecha de entrega' },
        total: { title: 'Total' },
      },
      labelsDetails: {
        work_order: { title: 'OT' },
        created_at: { title: 'Creada' },
        completed_at: { title: 'Completada' },
        sales_note_name: { title: 'Proyecto' },
        customer_name: { title: 'Cliente' },
        seller: { title: 'Vendedor' },
        sales_note__origen: { title: 'Origen' },
        delivery_date: { title: 'Fecha de entrega' },
        sku: { title: 'SKU' },
        name: { title: 'Producto' },
        qty: { title: 'Cantidad' },
        width: { title: 'Ancho' },
        height: { title: 'Alto' },
        mts2: { title: 'Mts2' },
        unit_price: { title: 'Precio Unitario' },
        total: { title: 'Total' },
      },
      csvTitle: `export_orden_trabajo_money_${moment().format('DD-MM-YYYY_HHmm')}`
    }
  },
  methods: {
    async fetch() {
        this.loading = true
        let created_before = ''
        let created_after = ''
        if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('YYYY-MM-DD HH:mm')
        if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().endOf('day').format('YYYY-MM-DD HH:mm')

        let seller = this.filter.seller || ''
        if (this.$can && !this.$can('readAll', 'SalesNote')) {
          const user = this.$store.getters.getUser
          if (user && user.id) seller = user.id
        }

        const { sortBy, sortDesc } = this.options || {}
        const sort = (sortBy && sortBy.length) ? sortBy[0] : 'created_at'
        const statusVal = (this.filter.status !== undefined && this.filter.status !== null) ? this.filter.status : ''

        const query = `page=1&page_size=10000&ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&search=${encodeURIComponent(this.search || '')}&created_after=${created_after}&created_before=${created_before}&delivery_after=${this.filter.delivery_after || ''}&delivery_before=${this.filter.delivery_before || ''}&completed=${statusVal}&sales_note__customer__seller=${seller}&sales_note__reposicion=${this.filter.reposicion || ''}`
        
        this.workOrdersDetail = []
        await fetchWorkOrdersMoney(query)
        .then(data => {
            this.workOrders = data.results.map(item => {
              item.created_at = moment(item.created_at).format('DD-MM-YYYY')
              item.delivery_date = moment(item.delivery_date).format('DD-MM-YYYY')
              item.completed_at = item.completed_at ? moment(item.completed_at).format('DD-MM-YYYY') : ''
              item.total = Math.round(item.totals?.total || 0)
              
              if (item.products) {
                item.products.forEach(p => {
                  this.workOrdersDetail.push({
                    work_order: item.id,
                    customer_name: item.sales_note__customer__name,
                    sales_note_name: item.sales_note__name,
                    seller: item.sales_note__customer__seller__first_name,
                    created_at: item.created_at,
                    delivery_date: item.delivery_date,
                    completed_at: item.completed_at,
                    sku: p.sku,
                    name: p.name,
                    qty: p.qty,
                    width: p.width,
                    height: p.height,
                    mts2: p.mts2,
                    unit_price: Math.round(p.qty != 0 ? (p.discountedTotalPrice / p.qty) : 0),
                    total: Math.round(p.discountedTotalPrice),
                    sales_note__origen: item.sales_note__origen
                  })
                })
              }
              return item
            })
            this.dataReady = true
        })
        .catch(err => notifications.showError('Error al exportar reporte monetario: ' + err))
        .finally(() => this.loading = false)
    },
    handleSuccess(val) {
      notifications.showSuccess('Reporte Monetario Exportado')
      this.dataReady = false
    },
    handleError(val) {
      notifications.showError('Error Exportando Reporte Monetario')
      this.dataReady = false
    }
  }
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; font-family: 'Outfit', sans-serif !important; }
.bg-grey-light { background-color: #f8fafc !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06) !important; }
.border-white-sm { border: 2px solid white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important; }
.h-44 { height: 44px !important; }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 1.5px; }
.tracking-wider { letter-spacing: 0.8px; }
.gap-3 { gap: 12px; }
</style>